import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Pagination } from '@/common/types';
import { API_URL, IServiceDto } from '@/dtos';
import { ALL_SERVICE_TYPES_KEY } from '@/modules/ProcessingDefinition/Services/Services.constants';
import {
  CategoryTypeDto,
  ServiceItem,
  ServiceTypeValues,
} from '@/modules/ProcessingDefinition/Services/Services.types';

export const QUERY_KEY = 'GET_SERVICES_BY_CATEGORY';

interface Params {
  categoryType: CategoryTypeDto;
  name?: string;
  serviceType?: string;
  page?: number;
  size?: number;
}

const useGetServicesByCategoryQuery = (params: Params, enabled = true) => {
  const { categoryType, name, serviceType, page = 0, size = 10 } = params;

  const { data, isLoading, refetch } = useQuery(
    [QUERY_KEY, categoryType, name, serviceType, page, size],
    () =>
      api.get<Pagination<IServiceDto>>(API_URL.V2_SERVICES_ALL, {
        params: {
          category_types: categoryType,
          ...(name && { name }),
          ...(serviceType && serviceType !== ALL_SERVICE_TYPES_KEY && { service_types: serviceType }),
          page,
          size,
        },
      }),
    {
      enabled: !!categoryType && enabled,
    }
  );

  const services = useMemo<ServiceItem[]>(() => {
    if (!data?.data?.content) return [];

    return data.data.content.map((item) => ({
      id: toString(item.id),
      name: item.name || '',
      serviceType: item.service_type || ServiceTypeValues.MANUAL_CLAIM,
      categoryType: categoryType,
    }));
  }, [data, categoryType]);

  const total = useMemo(() => data?.data?.totalElements || 0, [data]);

  return { services, isLoading, total, refetch };
};

export default useGetServicesByCategoryQuery;
